// <pos-workflow-editor/> — 訂單流程設定（owner only）
// Phase 2d · 表格式編輯器（不用拖拉 graph，小店家直覺）
// see docs/WORKFLOW_DESIGN_v0.1.md § UI 編輯器 Wireframe
//
// 策略：
// - 讀 workflow 走 workflow-cache（Dexie + ETag）
// - 寫入直接走 supabase client（RLS 幫把關：owner only、reserved fields freeze trigger）
// - 每次寫入後 invalidate Dexie cache 並觸發 workflow-updated event（pos-order-list 等
//   會跟著 re-render）
// - 不在此編輯器動 seed state 的 code / flags（trigger 會擋）；只能改 label / color / sort_order

import { getSupabase } from '../lib/supabase-client.js';
import { getWorkflow, WORKFLOW_EVENT_UPDATED } from '../lib/workflow-cache.js';
import { db } from '../lib/db.js';

const COLOR_PRESETS = [
  '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#6B7280',
  '#EF4444', '#F97316', '#60A5FA', '#14B8A6', '#EC4899',
];
const ROLE_OPTIONS = ['owner', 'manager', 'cashier', 'kitchen'];

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const escapeAttr = escapeHtml;

class PosWorkflowEditor extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      workflow: null,
      tab: 'states',      // 'states' | 'transitions'
      saving: false,
    };
    this._onUpdated = () => this.reload();
  }

  connectedCallback() {
    const role = document.body.dataset.role || '';
    if (role !== 'owner') { location.hash = '#/dashboard'; return; }
    window.addEventListener(WORKFLOW_EVENT_UPDATED, this._onUpdated);
    this.reload();
  }

  disconnectedCallback() {
    window.removeEventListener(WORKFLOW_EVENT_UPDATED, this._onUpdated);
  }

  async reload() {
    this.state.loading = true;
    this.state.error = null;
    this.render();
    try {
      const tenantId = document.body.dataset.tenantId;
      if (!tenantId) throw new Error('找不到 tenant');
      // 強制 invalidate cache，確保看最新版
      await db.workflow_cache.delete(tenantId);
      this.state.workflow = await getWorkflow(tenantId);
    } catch (err) {
      this.state.error = err.message_zh || err.message || '讀取流程失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async invalidateAndReload() {
    const tenantId = document.body.dataset.tenantId;
    if (tenantId) await db.workflow_cache.delete(tenantId);
    await this.reload();
  }

  // ───────── state CRUD（走 supabase client + RLS）─────────
  async updateState(stateId, patch) {
    const supabase = getSupabase();
    this.state.saving = true; this.render();
    try {
      const { error } = await supabase.from('workflow_states').update(patch).eq('id', stateId);
      if (error) throw error;
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '儲存失敗'}`);
    } finally {
      this.state.saving = false;
      this.render();
    }
  }

  async addState({ code, label, color }) {
    const wf = this.state.workflow;
    if (!wf) return;
    const tenantId = document.body.dataset.tenantId;
    const maxSort = Math.max(...wf.states.map(s => s.sort_order), -1);
    const supabase = getSupabase();
    try {
      const { error } = await supabase.from('workflow_states').insert({
        tenant_id: tenantId,
        workflow_id: wf.id,
        code, label, color,
        sort_order: maxSort + 1,
      });
      if (error) throw error;
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '新增失敗'}`);
    }
  }

  async deleteState(stateId) {
    const supabase = getSupabase();
    const state = this.state.workflow.states.find(s => s.id === stateId);
    if (!state) return;

    const tenantId = document.body.dataset.tenantId;

    // UX friction 2026-04-23 (Codex D)：刪前三層檢查
    // 1. OLD 自己有訂單
    // 2. CASCADE 會刪的 transitions
    // 3. Upstream recursive dead-set（跟 server trigger 20260611000070 同邏輯）
    const [{ count: orderCount }, refTrans, upstreamOrphans] = await Promise.all([
      supabase.from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', state.code),
      Promise.resolve(this.state.workflow.transitions.filter(
        t => t.from_state === stateId || t.to_state === stateId
      )),
      this.computeUpstreamOrphans(stateId, tenantId, supabase),
    ]);

    let msg = `確定刪除狀態「${state.label}」（code: ${state.code}）？`;
    if (orderCount > 0) {
      msg += `\n\n⚠️ 有 ${orderCount} 筆訂單卡在此狀態，DB 會擋下刪除，先結清或轉到其他狀態。`;
    }
    if (upstreamOrphans.length > 0) {
      const lines = upstreamOrphans.map(o => `  - ${o.label}（${o.count} 筆活訂單會卡住）`);
      msg += `\n\n⚠️ 刪除後以下狀態會失去所有出路，DB 會擋下：\n${lines.join('\n')}\n建議先為這些狀態加替代轉換規則，或結清訂單。`;
    }
    if (refTrans.length > 0) {
      const lines = refTrans.slice(0, 5).map(t => {
        const other = t.from_state === stateId
          ? `→ ${this.state.workflow.states.find(s => s.id === t.to_state)?.label}`
          : `← ${this.state.workflow.states.find(s => s.id === t.from_state)?.label}`;
        return `  ${other}（${t.label || '(無按鈕文字)'}）`;
      });
      const more = refTrans.length > 5 ? `\n  ...還有 ${refTrans.length - 5} 條` : '';
      msg += `\n\n會一併刪除 ${refTrans.length} 條 transition：\n${lines.join('\n')}${more}`;
    }
    msg += '\n\n繼續？';

    if (!confirm(msg)) return;

    try {
      const { error } = await supabase.from('workflow_states').delete().eq('id', stateId);
      if (error) {
        // DB trigger check_state_not_in_active_orders 會擋
        throw new Error(error.message || '刪除失敗');
      }
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message}`);
    }
  }

  /**
   * 用 iterative fixed-point 算刪除 stateId 後會變 dead-end 的 upstream states
   * （邏輯對齊 server migration 20260611000070 的 trigger），再對每個 query 活訂單數
   * @returns [{code, label, count}, ...]
   */
  async computeUpstreamOrphans(deletedStateId, tenantId, supabase) {
    const wf = this.state.workflow;
    if (!wf) return [];

    const deadSet = new Set([deletedStateId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of wf.states) {
        if (deadSet.has(s.id)) continue;
        if (s.is_terminal_success || s.is_terminal_cancel) continue;
        const outs = wf.transitions.filter(t => t.from_state === s.id);
        if (outs.length === 0) continue;  // 原本無 out 不算此次影響
        if (outs.every(t => deadSet.has(t.to_state))) {
          deadSet.add(s.id);
          grew = true;
        }
      }
    }
    deadSet.delete(deletedStateId);  // 自己不算 upstream

    // 對 dead set 中每個 state 查活訂單
    const results = [];
    for (const id of deadSet) {
      const ws = wf.states.find(s => s.id === id);
      if (!ws) continue;
      const { count } = await supabase.from('orders')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', ws.code);
      if ((count || 0) > 0) {
        results.push({ code: ws.code, label: ws.label, count });
      }
    }
    return results;
  }

  async reorderState(stateId, direction) {
    const states = [...this.state.workflow.states].sort((a, b) => a.sort_order - b.sort_order);
    const idx = states.findIndex(s => s.id === stateId);
    const other = direction === 'up' ? idx - 1 : idx + 1;
    if (other < 0 || other >= states.length) return;
    const a = states[idx], b = states[other];
    const supabase = getSupabase();
    try {
      // 先設 a 到一個暫時值避開 unique（本表沒 unique on sort_order，直接 swap 就好）
      await supabase.from('workflow_states').update({ sort_order: b.sort_order }).eq('id', a.id);
      await supabase.from('workflow_states').update({ sort_order: a.sort_order }).eq('id', b.id);
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '排序失敗'}`);
    }
  }

  // ───────── transition CRUD ─────────
  async addTransition({ from_state, to_state, label, allowed_roles }) {
    const wf = this.state.workflow;
    if (!wf) return;
    const tenantId = document.body.dataset.tenantId;
    const supabase = getSupabase();
    try {
      const { error } = await supabase.from('workflow_transitions').insert({
        tenant_id: tenantId,
        workflow_id: wf.id,
        from_state, to_state, label,
        allowed_roles,
      });
      if (error) throw error;
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '新增失敗（可能已存在相同轉換）'}`);
    }
  }

  async updateTransition(id, patch) {
    const supabase = getSupabase();
    try {
      const { error } = await supabase.from('workflow_transitions').update(patch).eq('id', id);
      if (error) throw error;
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '儲存失敗'}`);
    }
  }

  async deleteTransition(id) {
    if (!confirm('確定刪除此轉換規則？')) return;
    const supabase = getSupabase();
    try {
      const { error } = await supabase.from('workflow_transitions').delete().eq('id', id);
      if (error) throw error;
      await this.invalidateAndReload();
    } catch (err) {
      alert(`✗ ${err.message || '刪除失敗'}`);
    }
  }

  // ───────── render ─────────
  render() {
    const { loading, error, workflow, tab, saving } = this.state;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 1100px; margin: 0 auto; color: var(--fg); }
        h2 { font-family: var(--font-display); margin: 0 0 var(--s-4); color: var(--brand-primary); }
        .tabs { display: flex; gap: var(--s-1); margin-bottom: var(--s-4); border-bottom: 1px solid var(--border); }
        .tabs button {
          background: transparent; border: 0; border-bottom: 2px solid transparent;
          padding: var(--s-2) var(--s-4); cursor: pointer; font: inherit; color: var(--fg-muted);
        }
        .tabs button[aria-selected="true"] {
          color: var(--brand-primary); border-bottom-color: var(--brand-primary); font-weight: 600;
        }
        .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); margin-bottom: var(--s-3); }
        .banner.err { background: rgba(220,38,38,.1); color: var(--danger); }
        .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }

        table { width: 100%; border-collapse: collapse; background: var(--bg-elevated); border-radius: var(--r-lg); overflow: hidden; }
        th, td { padding: var(--s-3) var(--s-4); text-align: left; border-bottom: 1px solid var(--border); font-size: .9rem; vertical-align: middle; }
        th { background: var(--bg-surface); color: var(--fg-muted); font-size: .75rem; font-weight: 500; text-transform: uppercase; letter-spacing: .05em; }
        tr:last-child td { border-bottom: 0; }
        input[type="text"], select, input[type="color"] {
          background: var(--bg); color: var(--fg); border: 1px solid var(--border);
          border-radius: var(--r-md); padding: var(--s-2) var(--s-3); font: inherit; min-height: 32px;
        }
        input[type="text"] { width: 140px; }
        input[type="color"] { width: 50px; padding: 0; cursor: pointer; }
        .lock { color: var(--fg-muted); font-size: .85rem; }
        .icon-btn {
          padding: var(--s-1) var(--s-2); background: transparent; border: 1px solid var(--border);
          border-radius: var(--r-sm); cursor: pointer; font: inherit; color: var(--fg);
        }
        .icon-btn:hover { background: var(--bg-elevated); }
        .icon-btn.danger { color: var(--danger); }
        .icon-btn[disabled] { opacity: .4; cursor: not-allowed; }
        .dot { display: inline-block; width: 16px; height: 16px; border-radius: 50%; vertical-align: middle; margin-right: 6px; }
        .add-row { background: var(--bg-surface); }
        .flag-pill {
          display: inline-block; padding: 2px 6px; border-radius: var(--r-sm); font-size: .7rem;
          background: rgba(99,102,241,.15); color: var(--brand-primary); margin-left: 4px;
        }
        .role-checks { display: flex; gap: var(--s-2); flex-wrap: wrap; }
        .role-checks label { display: inline-flex; align-items: center; gap: 4px; font-size: .85rem; }
        .hint { color: var(--fg-muted); font-size: .8rem; margin-top: var(--s-2); }
        .primary { background: var(--brand-primary); color: white; border-color: var(--brand-primary); }
      </style>

      <h2>訂單流程設定</h2>

      ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
      ${loading ? `<div class="banner loading">載入中…</div>` : ''}
      ${saving ? `<div class="banner loading">儲存中…</div>` : ''}

      <div class="tabs" role="tablist">
        <button role="tab" data-tab="states" aria-selected="${tab === 'states'}">狀態清單</button>
        <button role="tab" data-tab="transitions" aria-selected="${tab === 'transitions'}">轉換規則</button>
      </div>

      ${workflow && tab === 'states' ? this.renderStatesTab(workflow) : ''}
      ${workflow && tab === 'transitions' ? this.renderTransitionsTab(workflow) : ''}

      <p class="hint">🔒 標記的狀態為系統保留（付款完成 / 已完成 / 已取消 / 已退款），可改顯示名與顏色，不可刪除或改代碼。</p>
    `;

    this.wireEvents();
  }

  renderStatesTab(workflow) {
    const states = [...workflow.states].sort((a, b) => a.sort_order - b.sort_order);
    return `
      <table>
        <thead>
          <tr>
            <th style="width:80px">排序</th>
            <th style="width:140px">代碼</th>
            <th>顯示名</th>
            <th style="width:80px">顏色</th>
            <th>旗標</th>
            <th style="width:80px">操作</th>
          </tr>
        </thead>
        <tbody>
          ${states.map((s, i) => {
            const flags = [
              s.is_payment_complete ? '已收款' : null,
              s.is_terminal_success ? '完成終態' : null,
              s.is_terminal_cancel ? '取消終態' : null,
            ].filter(Boolean);
            return `
              <tr data-state-id="${s.id}">
                <td>
                  <button class="icon-btn" data-act="up" data-id="${s.id}" ${i === 0 ? 'disabled' : ''}>↑</button>
                  <button class="icon-btn" data-act="down" data-id="${s.id}" ${i === states.length - 1 ? 'disabled' : ''}>↓</button>
                </td>
                <td><code>${escapeHtml(s.code)}</code> ${!s.is_deletable ? '<span class="lock">🔒</span>' : ''}</td>
                <td>
                  <input type="text" data-field="label" data-id="${s.id}" value="${escapeAttr(s.label)}" maxlength="40" />
                </td>
                <td>
                  <input type="color" data-field="color" data-id="${s.id}" value="${escapeAttr(s.color)}" />
                </td>
                <td>${flags.map(f => `<span class="flag-pill">${f}</span>`).join('')}</td>
                <td>
                  ${s.is_deletable
                    ? `<button class="icon-btn danger" data-act="del-state" data-id="${s.id}">✕</button>`
                    : '<span class="lock">—</span>'}
                </td>
              </tr>
            `;
          }).join('')}
          <tr class="add-row">
            <td colspan="2">
              <input type="text" id="new-state-code" placeholder="代碼（英數底線）" maxlength="40" />
            </td>
            <td>
              <input type="text" id="new-state-label" placeholder="顯示名" maxlength="40" />
            </td>
            <td>
              <input type="color" id="new-state-color" value="#60A5FA" />
            </td>
            <td></td>
            <td>
              <button class="icon-btn primary" data-act="add-state">+ 新增</button>
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  renderTransitionsTab(workflow) {
    const statesById = new Map(workflow.states.map(s => [s.id, s]));
    const transitions = [...workflow.transitions].sort((a, b) => a.sort_order - b.sort_order);
    const stateOptions = workflow.states
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(s => `<option value="${s.id}">${escapeHtml(s.label)}</option>`).join('');

    return `
      <table>
        <thead>
          <tr>
            <th>從</th><th>到</th><th>按鈕文字</th><th>允許角色</th><th style="width:80px">操作</th>
          </tr>
        </thead>
        <tbody>
          ${transitions.map(t => {
            const from = statesById.get(t.from_state);
            const to = statesById.get(t.to_state);
            const roles = t.allowed_roles || [];
            return `
              <tr data-transition-id="${t.id}">
                <td>${escapeHtml(from?.label || '?')}</td>
                <td>${escapeHtml(to?.label || '?')}</td>
                <td>
                  <input type="text" data-field="label" data-tid="${t.id}" value="${escapeAttr(t.label || '')}" placeholder="按鈕文字" maxlength="40" />
                </td>
                <td>
                  <div class="role-checks">
                    ${ROLE_OPTIONS.map(r => `
                      <label><input type="checkbox" data-field="role" data-tid="${t.id}" data-role="${r}" ${roles.includes(r) ? 'checked' : ''}/> ${r}</label>
                    `).join('')}
                  </div>
                </td>
                <td>
                  <button class="icon-btn danger" data-act="del-transition" data-id="${t.id}">✕</button>
                </td>
              </tr>
            `;
          }).join('')}
          <tr class="add-row">
            <td><select id="new-tr-from">${stateOptions}</select></td>
            <td><select id="new-tr-to">${stateOptions}</select></td>
            <td><input type="text" id="new-tr-label" placeholder="按鈕文字" maxlength="40" /></td>
            <td>
              <div class="role-checks">
                ${ROLE_OPTIONS.map(r => `
                  <label><input type="checkbox" data-new-role="${r}" ${r !== 'kitchen' ? 'checked' : ''}/> ${r}</label>
                `).join('')}
              </div>
            </td>
            <td><button class="icon-btn primary" data-act="add-transition">+ 新增</button></td>
          </tr>
        </tbody>
      </table>
    `;
  }

  wireEvents() {
    const root = this.shadowRoot;

    // Tab 切換
    root.querySelectorAll('[role="tab"]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.state.tab = btn.dataset.tab;
        this.render();
      });
    });

    // State inline label / color edit（blur / change 觸發）
    root.querySelectorAll('input[data-field="label"][data-id]').forEach(inp => {
      inp.addEventListener('change', (e) => {
        const id = inp.dataset.id;
        const row = this.state.workflow.states.find(s => s.id === id);
        if (row && row.label !== e.target.value) {
          this.updateState(id, { label: e.target.value });
        }
      });
    });
    root.querySelectorAll('input[data-field="color"][data-id]').forEach(inp => {
      inp.addEventListener('change', (e) => {
        const id = inp.dataset.id;
        const row = this.state.workflow.states.find(s => s.id === id);
        if (row && row.color !== e.target.value) {
          this.updateState(id, { color: e.target.value });
        }
      });
    });

    // State action buttons
    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', () => {
        const act = btn.dataset.act;
        if (act === 'up' || act === 'down') this.reorderState(btn.dataset.id, act);
        else if (act === 'del-state') this.deleteState(btn.dataset.id);
        else if (act === 'add-state') {
          const code = root.querySelector('#new-state-code').value.trim().toLowerCase();
          const label = root.querySelector('#new-state-label').value.trim();
          const color = root.querySelector('#new-state-color').value;
          if (!code || !label) return alert('代碼與顯示名必填');
          if (!/^[a-z0-9_]+$/.test(code)) return alert('代碼只允許小寫英數和底線');
          this.addState({ code, label, color });
        }
        else if (act === 'del-transition') this.deleteTransition(btn.dataset.id);
        else if (act === 'add-transition') {
          const from_state = root.querySelector('#new-tr-from').value;
          const to_state = root.querySelector('#new-tr-to').value;
          const label = root.querySelector('#new-tr-label').value.trim() || null;
          const allowed_roles = [...root.querySelectorAll('[data-new-role]')]
            .filter(c => c.checked).map(c => c.dataset.newRole);
          if (from_state === to_state) return alert('起始狀態與目標狀態不可相同');
          if (allowed_roles.length === 0) return alert('請至少選一個允許的角色');
          this.addTransition({ from_state, to_state, label, allowed_roles });
        }
      });
    });

    // Transition inline label edit
    root.querySelectorAll('input[data-field="label"][data-tid]').forEach(inp => {
      inp.addEventListener('change', (e) => {
        const id = inp.dataset.tid;
        const row = this.state.workflow.transitions.find(t => t.id === id);
        if (row && (row.label || '') !== e.target.value) {
          this.updateTransition(id, { label: e.target.value || null });
        }
      });
    });

    // Transition role checkbox change
    root.querySelectorAll('input[data-field="role"][data-tid]').forEach(cb => {
      cb.addEventListener('change', () => {
        const id = cb.dataset.tid;
        const row = this.state.workflow.transitions.find(t => t.id === id);
        if (!row) return;
        const roles = [...this.shadowRoot.querySelectorAll(`input[data-tid="${id}"][data-field="role"]`)]
          .filter(c => c.checked).map(c => c.dataset.role);
        if (roles.length === 0) {
          alert('至少保留一個允許角色');
          cb.checked = true;
          return;
        }
        this.updateTransition(id, { allowed_roles: roles });
      });
    });
  }
}

customElements.define('pos-workflow-editor', PosWorkflowEditor);
